/**
 * Add a line to the bill for something that is not in the catalog.
 *
 * The shopkeeper types what they are selling, its HSN/SAC, how many and at
 * what rate; the GST slab follows from the HSN and the money is computed by
 * the same engine that computes every other line, so a manual line and a
 * catalog line cannot disagree about tax.
 *
 * Two small affordances carry most of the weight at a counter:
 *   - typing a name suggests HSN codes, so the code does not have to be known
 *   - choosing an HSN fills its GST slab, which is the part people get wrong
 * Both are suggestions. The slab stays editable, because the table is a help,
 * not an authority, and the merchant is the one who signs the return.
 */
import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { NumberField, useNumberDraft } from '@/components/ui/NumberField';
import { Money } from '@/components/ui/numeric';
import { useSettingsStore } from '@/store/useSettingsStore';
import { getCartConfig } from '@/store/useCartStore';
import { computeLineItem, toRupees, type ProductForBilling } from '@/billing/billingEngine';
import { toStoredSellingPrice } from '@/billing/priceMode';
import {
  buildManualBillingProduct,
  gstRateForHsn,
  hsnSuggestionsForName,
  type RememberedManualItem,
} from '@/billing/manualItem';
import { VALID_GST_RATES } from '@bigxbook/gst-core';
import { cn } from '@/utils/cn';

/** Units offered in the picker. */
const UNITS = ['PCS', 'NOS', 'KG', 'GM', 'LTR', 'ML', 'MTR', 'FT', 'BOX', 'SET', 'PKT', 'BAG'];

const HSN_PATTERN = /^\d{4}$|^\d{6}$|^\d{8}$/;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isInterState: boolean;
  /** Prefills the description - used when a search found nothing. */
  initialName?: string;
  /**
   * A manual item this store has billed before. Its saved details fill the
   * form, but the form still opens: the rate and quantity of a non-catalog
   * line are decisions for THIS bill, not properties of a product, so they
   * are confirmed every time rather than replayed silently.
   */
  prefill?: RememberedManualItem | null;
  onAdd: (product: ProductForBilling, qty: number) => void;
}

export function ManualItemDialog({
  open,
  onOpenChange,
  isInterState,
  initialName = '',
  prefill = null,
  onAdd,
}: Props) {
  const [name, setName] = useState(initialName);
  const [hsn, setHsn] = useState('');
  const [qty, setQty] = useState(1);
  const [rate, setRate] = useState(0);
  const [gstRate, setGstRate] = useState(18);
  const [unit, setUnit] = useState('PCS');
  const [rateIncludesGst, setRateIncludesGst] = useState(true);
  /** Set once the slab has been chosen by hand - stops HSN from overwriting it. */
  const [gstTouched, setGstTouched] = useState(false);

  const storeIsInclusive = useSettingsStore((s) => s.getBoolean('TAX_INCLUSIVE'));
  const defaultInclGst = useSettingsStore((s) => s.getBoolean('PRODUCT_PRICE_INCLUDES_GST'));

  // Reopening is a fresh line - never inherit the last one's numbers. A
  // remembered item supplies its own, and marks the slab as already decided so
  // the HSN lookup does not overwrite what the shop actually charged.
  useEffect(() => {
    if (!open) return;
    setName(prefill?.name ?? initialName);
    setHsn(prefill?.hsn ?? '');
    setQty(1);
    setRate(prefill?.rate ?? 0);
    setGstRate(prefill?.gstRate ?? 18);
    setUnit(prefill?.unit ?? 'PCS');
    setGstTouched(prefill != null);
    // A remembered rate was stored in the shop's own convention, so it is
    // shown in that convention rather than through the entry-mode default.
    setRateIncludesGst(prefill ? storeIsInclusive : defaultInclGst);
  }, [open, initialName, prefill, defaultInclGst, storeIsInclusive]);

  // HSN drives the slab until the operator overrides it.
  useEffect(() => {
    if (gstTouched) return;
    const found = gstRateForHsn(hsn);
    if (found != null) setGstRate(found);
  }, [hsn, gstTouched]);

  const suggestions = useMemo(
    () => (hsn.trim() === '' ? hsnSuggestionsForName(name) : []),
    [name, hsn],
  );

  const hsnValid = hsn.trim() === '' || HSN_PATTERN.test(hsn.trim());
  const canAdd = name.trim().length > 0 && qty > 0 && rate > 0 && hsnValid;

  /**
   * The live preview runs the REAL engine on the REAL settings, so what the
   * dialog promises is what the bill charges. Building the product here also
   * means the rate conversion is exercised in the preview, not only on submit.
   */
  const line = useMemo(() => {
    const storedRate = toStoredSellingPrice(rate, gstRate, 0, rateIncludesGst, storeIsInclusive);
    const product = buildManualBillingProduct({
      name: name || 'Item',
      hsn: hsn || null,
      rate: storedRate,
      gstRate,
      unit,
    });
    return computeLineItem(product, qty || 0, storedRate, 0, isInterState, getCartConfig());
  }, [name, hsn, qty, rate, gstRate, unit, rateIncludesGst, storeIsInclusive, isInterState]);

  const rateProps = useNumberDraft({
    value: rate,
    onCommit: setRate,
    decimals: true,
    blankZero: true,
  });

  const submit = () => {
    if (!canAdd) return;
    // Rebuilt rather than reused from the preview: the preview is memoised, and
    // a fresh object guarantees a fresh cart id for every line added.
    const storedRate = toStoredSellingPrice(rate, gstRate, 0, rateIncludesGst, storeIsInclusive);
    onAdd(
      buildManualBillingProduct({
        name: name.trim(),
        hsn: hsn.trim() || null,
        rate: storedRate,
        gstRate,
        unit,
      }),
      qty,
    );
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Add an item not in inventory" className="w-[560px]">
        <div
          className="space-y-4 px-5 py-4"
          onKeyDown={(e) => {
            // Enter anywhere in the form adds the line - a counter does not
            // reach for the mouse.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        >
          <Field label="Item name / description" hint="This is what prints on the bill">
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Fitting charges, brass nipple 1/2 inch"
            />
          </Field>

          {suggestions.length > 0 && (
            <div className="space-y-1">
              <span className="text-label uppercase text-fg-tertiary">
                Suggested HSN for this description
              </span>
              <div className="flex flex-wrap gap-1.5">
                {suggestions.map((s) => (
                  <button
                    key={`${s.code}-${s.gstRate}`}
                    type="button"
                    onClick={() => {
                      setHsn(s.code);
                      if (!gstTouched) setGstRate(s.gstRate);
                    }}
                    className="rounded-md border border-stroke-hairline/20 bg-bg-canvas px-2 py-1 text-label text-fg-secondary transition-colors hover:border-accent/40 hover:text-fg-primary"
                  >
                    <span className="font-medium tabular-nums">{s.code}</span>
                    <span className="mx-1 text-fg-tertiary">·</span>
                    {s.description}
                    <span className="ml-1 tabular-nums text-accent-ink">{s.gstRate}%</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field
              label="HSN / SAC"
              hint={
                hsn.trim() !== '' && !hsnValid
                  ? 'An HSN is 4, 6 or 8 digits'
                  : 'Sets the GST slab beside it'
              }
            >
              <Input
                value={hsn}
                onChange={(e) => setHsn(e.target.value.replace(/\D/g, '').slice(0, 8))}
                inputMode="numeric"
                invalid={hsn.trim() !== '' && !hsnValid}
                placeholder="e.g. 7307"
                className="tabular-nums"
              />
            </Field>

            <Field label="GST rate">
              <select
                value={gstRate}
                onChange={(e) => {
                  setGstRate(Number(e.target.value));
                  setGstTouched(true);
                }}
                className="h-9 w-full rounded-md border border-stroke-hairline/15 bg-white/70 px-3 text-body tabular-nums text-fg-primary outline-none focus:border-accent/40 focus:bg-white"
              >
                {VALID_GST_RATES.map((r) => (
                  <option key={r} value={r}>
                    {r}%
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Quantity">
              <NumberField
                aria-label="Manual item quantity"
                value={qty}
                onCommit={setQty}
                commitOn="blur"
                min={1}
                className="h-9 w-full rounded-md border border-stroke-hairline/15 bg-white/70 px-3 text-right text-body text-fg-primary outline-none focus:border-accent/40 focus:bg-white"
              />
            </Field>

            <Field label="Unit">
              <select
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                className="h-9 w-full rounded-md border border-stroke-hairline/15 bg-white/70 px-3 text-body text-fg-primary outline-none focus:border-accent/40 focus:bg-white"
              >
                {UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Rate" hint={rateIncludesGst ? 'Including GST' : 'Before GST'}>
              <Input {...rateProps} placeholder="0.00" className="text-right tabular-nums" />
            </Field>
          </div>

          <label className="flex items-center gap-2 text-body text-fg-secondary">
            <input
              type="checkbox"
              checked={rateIncludesGst}
              onChange={(e) => setRateIncludesGst(e.target.checked)}
              className="h-4 w-4 accent-accent"
            />
            The rate I typed includes GST
          </label>

          {/* What this line will do to the bill, straight from the engine. */}
          <div className="rounded-lg border border-stroke-hairline/15 bg-bg-subtle/60 px-3 py-2.5">
            <PreviewRow label="Taxable value" value={toRupees(line.taxableAmountPaisa)} />
            {isInterState ? (
              <PreviewRow label={`IGST ${gstRate}%`} value={toRupees(line.igstPaisa)} muted />
            ) : (
              <>
                <PreviewRow label={`CGST ${gstRate / 2}%`} value={toRupees(line.cgstPaisa)} muted />
                <PreviewRow label={`SGST ${gstRate / 2}%`} value={toRupees(line.sgstPaisa)} muted />
              </>
            )}
            <div className="mt-1.5 border-t border-stroke-hairline/15 pt-1.5">
              <PreviewRow label="Line total" value={toRupees(line.totalPaisa)} strong />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={!canAdd}>
              <Plus size={14} className="mr-1" />
              Add to bill
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-label uppercase text-fg-tertiary">{label}</span>
      {children}
      {hint && <span className="text-label text-fg-tertiary">{hint}</span>}
    </label>
  );
}

function PreviewRow({
  label,
  value,
  muted,
  strong,
}: {
  label: string;
  value: number;
  muted?: boolean;
  strong?: boolean;
}) {
  return (
    <div className="flex items-center justify-between text-body">
      <span className={muted ? 'text-fg-secondary' : 'text-fg-primary'}>{label}</span>
      <Money value={value} className={cn(strong && 'font-semibold')} />
    </div>
  );
}
